import React from 'react';
import LmsRolePage from './LmsRolePage';

const LmsAviationAuthority: React.FC = () => {
  return (
    <LmsRolePage
      title="Competency Governance for Aviation Authorities"
      seoTitle="Competency Governance for Aviation Authorities"
      description="ICAO competency frameworks enforced through traceable training evidence, qualification governance, and authority-ready oversight workflows."
      eyebrow="Aviation Authorities"
      heroNote="Designed for CAAs and ANSPs. Not a training marketplace."
      heroImagePrompt="A formal, monochrome governance document layout showing competency records, verification stamps, ATCO objective mapping, and structured evidence folders, institutional tone, no UI elements"
      heroImageAlt="Aviation authority competency governance view showing training evidence, qualification controls, and verification workflows"
      painPoints={[
        'Training records disconnected from SMS findings and formal oversight workflows.',
        'No proof of competency effectiveness beyond attendance and certificate issue.',
        'Audits rely on spreadsheets, fragmented evidence, and manual verification.',
        'No durable linkage between role, risk, objective coverage, and qualification status.',
      ]}
      capabilities={[
        'Role-based competency and qualification structures with recurrence and validity controls.',
        'Objective-aware training evidence connected to governed qualification records.',
        'Authority verification flows with assessor authority and audit-ready record history.',
        'Segregated organizational control with oversight-first access patterns.',
      ]}
      workflow={[
        {
          title: 'Define the governance model',
          description: 'Authorities establish the qualification structure, recurrence, and evidence rules that regulated organizations must follow.',
        },
        {
          title: 'Capture training evidence',
          description: 'Training activity, assessments, certificates, and objective evidence are retained as governed records instead of isolated LMS events.',
        },
        {
          title: 'Verify and oversee',
          description: 'Auditors and authority reviewers see traceable evidence, current qualification status, and clear linkage to safety-driven training action.',
        },
      ]}
      outcomes={[
        'Evidence-first oversight instead of attendance-first reporting.',
        'Clearer audit defensibility across competency, currency, and verification.',
        'A governed bridge between training remediation and authority review.',
      ]}
      keywords={[
        'regulatory LMS',
        'aviation authority LMS',
        'CAA training oversight',
        'competency governance aviation',
        'ATCO evidence management',
      ]}
      faq={[
        {
          question: 'Is this page focused on oversight rather than generic e-learning?',
          answer: 'Yes. The authority positioning emphasizes competency governance, evidence traceability, recurrence, and verification workflows rather than open marketplace-style course delivery.',
        },
        {
          question: 'Can authorities verify training evidence instead of only certificates?',
          answer: 'Yes. AviaGov is positioned around governed training evidence, qualification records, and assessor or authority review controls so oversight does not depend on certificates alone.',
        },
      ]}
      relatedLinks={[
        { label: 'LMS for ANSPs and CAAs', path: '/lms-ansp-caa' },
        { label: 'LMS for Airlines', path: '/lms-airlines' },
      ]}
      ctaId="lms_authority_walkthrough"
    />
  );
};

export default LmsAviationAuthority;
